import type { ClientPersonReference } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList } from 'react-native';

import { useGetHappeningParticipants } from '@/features/api/hooks/grapevine-happening/useGetHappeningParticipants';
import { ListScreenLayout } from '@/features/groups/components/members/ListScreenLayout';
import { ListItem } from '@/features/list/components/ListItem';
import { PersonAvatar } from '@/features/media/components/avatar/PersonAvatar';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import type {
  AppNativeStackScreenProps,
  AppRoute
} from '@/navigation/types/navigation';
import { Refresher } from '@/shared/components/state-view/Refresher';
import { getPersonName } from '@/shared/utils/person';

type Props = AppNativeStackScreenProps<typeof AppRoute.HappeningParticipants>;

export const HappeningParticipantsScreen: FC<Props> = ({ route }) => {
  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'screens.happening-participants'
  });

  const { postId, participantCount } = route.params;

  const {
    data: participants,
    isRefetching,
    refetch
  } = useGetHappeningParticipants(postId);

  return (
    <ListScreenLayout
      title={t('participants', {
        count: participants?.page?.totalElements ?? participantCount ?? 0
      })}
      list={
        <FlatList<ClientPersonReference>
          data={participants?.content}
          refreshing={isRefetching}
          onRefresh={refetch}
          refreshControl={<Refresher refreshing={isRefetching} />}
          renderItem={({ item: participant }) => (
            <ListItem
              style={{ paddingRight: 16, paddingVertical: 2 }}
              key={participant.id}
              icon={() => (
                <PersonAvatar person={participant} size={40} canOpenGallery />
              )}
              title={getPersonName(participant)}
              hideRightArrow
            />
          )}
          keyExtractor={(member, idx) => member.id ?? idx.toString()}
        />
      }
    />
  );
};
